import * as React from "react";
import {
    ColumnFiltersState,
    SortingState,
    VisibilityState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
    ColumnDef,
    ExpandedState,
    getExpandedRowModel,
} from "@tanstack/react-table";
import { Input } from "components/ui/input";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "components/ui/table";
import TablePagination from "./table-pagination";
import { useEffect, useState } from "react";
import {
    getProductDataAction,
    productDeleteAction,
    ProductList,
    productSetAvailabilityAction
} from "action/product-action";
import { toast } from "sonner";
import Loading from "components/loading";
import EditProductComponent, {
    EditProductProps,
} from "screens/partials/edit-product";
import { Checkbox } from "components/ui/checkbox";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "components/ui/tooltip";
import { Button } from "components/ui/button";
import { CircleCheck, Eye, PackageCheck, SquarePen, Trash2 } from "lucide-react";
import SiteBreadcrumb from "components/site-breadcrumb";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "components/ui/dialog";
// Import CSSTransition and TransitionGroup for animations
import { CSSTransition, TransitionGroup } from "react-transition-group";
import "./StorboltProductListingComponent.css";
import { CURRENCY } from "helpers/constants";
import { useSessionControl } from "hooks/use-session-control";
import { CircleMinus } from 'lucide-react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";


const StorboltProductListingComponent = () => {
    const [sorting, setSorting] = React.useState<SortingState>([]);
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
        []
    );
    const [columnVisibility, setColumnVisibility] =
        React.useState<VisibilityState>({});
    const [rowSelection, setRowSelection] = React.useState({});
    const [expanded, setExpanded] = React.useState<ExpandedState>({});
    const [data, setData] = React.useState<ProductList[]>([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [selectedProduct, setSelectedProduct] = useState<ProductList | null>(
        null
    );
    const [showList, setShowList] = useState(true);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [selectedProductsId, setSelectedProductsId] = useState<string[]>([]);
    const [productAvailability, setProductAvailability] = useState<string>("");
    const {sessionControl} = useSessionControl();

    useEffect(() => {
        setIsLoading(true);
        getProductDataAction()
            .then((response) => {
                if (response.status === 200) {
                    setData(response.body);
                    if (response.message) {
                        toast.error(response.message);
                    }
                } else {
                    sessionControl(response.status);
                    toast.error(response.error);
                }
                setIsLoading(false);
            })
            .catch((err: any) => {
                toast.error("Failed to get products, An error occurred");
                setIsLoading(false);
            });
    }, []);

    const renderView = (product: ProductList) => {
        const editProps: EditProductProps = {
            id: product.id as string,
            name: product.name,
            category: product.category,
            price: product.price,
            description: product.description,
            featured: product.featured,
            today_special: product.today_special,
            image_url: product.image_url,
        };
        return (
            <EditProductComponent
                {...editProps}
                onCancel={() => setShowList(true)}
                onUpdateDone={() => window.location.reload()}
            />
        );
    };

    const handleDeleteProduct = (product: ProductList) => {
        setSelectedProduct(product);
        setShowDeleteDialog(true);
    };

    const confirmDeleteProduct = () => {
        if (selectedProduct) {
            const toastId = toast.loading("Deleting product...");
            productDeleteAction(selectedProduct.id).then((response) => {
                if (response.status === 200) {
                    toast.success("Product deleted successfully", {
                        id: toastId,
                    });
                } else {
                    sessionControl(response.status);
                    toast.error(response.error, {
                        id: toastId,
                    });
                }
            })
            setData((prevData) =>
                prevData.filter((item) => item.id !== selectedProduct.id)
            );
            setShowDeleteDialog(false);
            setSelectedProduct(null);
        }
    };

    const cancelDeleteProduct = () => {
        setShowDeleteDialog(false);
        setSelectedProduct(null);
    };

    const handleAvailabilityRequest = (productIds: string[], isAvailable: boolean) => {
        const toastId = toast.loading("Updating availability...");
        return productSetAvailabilityAction(productIds, isAvailable).then((response => {
            if (response.status === 200) {
                toast.success("Availability updated successfully", {
                    id: toastId,
                });
            } else {
                sessionControl(response.status);
                toast.error(response.error, {
                    id: toastId,
                });
            }
        }))
    }

    const handleAvailabilityUpdate = async () => {
        if (productAvailability) {
            const availability = productAvailability === "available"; // true if "available", false if "unavailable"

            await handleAvailabilityRequest(selectedProductsId, availability);

            setData((prevData) =>
                prevData.map((item) =>
                    selectedProductsId.includes(item.id)
                        ? availability
                            ? {...item, available: availability}
                            : {...item, featured: availability, today_special: availability, available: availability}
                        : item
                )
            );

            setProductAvailability("");
        }
    };


    const columns: ColumnDef<ProductList>[] = [
        {
            id: "select",
            header: ({table}) => (
                <Checkbox
                    checked={table.getIsAllPageRowsSelected()}
                    onCheckedChange={(value) => {
                        table.toggleAllPageRowsSelected(!!value)
                        if (value) {
                            setSelectedProductsId(table.getRowModel().rows.map(row => row.original.id));
                        } else {
                            setSelectedProductsId([]);
                        }
                    }}
                    aria-label="Select all"
                />
            ),
            cell: ({row}) => (
                <div className="xl:w-16">
                    <Checkbox
                        onClick={(e) => {
                            e.stopPropagation();
                        }}
                        checked={row.getIsSelected()}
                        onCheckedChange={(value) => {
                            row.toggleSelected(!!value);
                            if (value) {
                                setSelectedProductsId([...selectedProductsId, row.original.id]);
                            } else {
                                setSelectedProductsId(selectedProductsId.filter(id => id !== row.original.id));
                            }
                        }}
                        aria-label="Select row"
                    />
                </div>
            ),
            enableSorting: false,
            enableHiding: false,
        },
        {
            accessorKey: "name",
            header: "Name",
            cell: ({row}) => <span>{row.getValue("name")}</span>,
        },
        {
            accessorKey: "price",
            header: "Price",
            cell: ({row}) => {
                return <span>{CURRENCY}{row.getValue("price")}</span>;
            },
        },
        {
            accessorKey: "category",
            header: "Category",
            cell: ({row}) => <span>{row.getValue("category")}</span>,
        },
        {
            accessorKey: "featured",
            header: () => <span className="flex items-center justify-center">Featured</span>,
            cell: ({row}) => {
                return <span className="flex items-center justify-center">{row.getValue("featured") ?
                    <CircleCheck className="w-4 h-4 text-green-500"/> :
                    <CircleMinus className="w-4 h-4 text-red-500"/>}</span>;
            },
        },
        {
            accessorKey: "today_special",
            header: () => <span className="flex items-center justify-center">Today Special</span>,
            cell: ({row}) => {
                return <span className="flex items-center justify-center">{row.getValue("today_special") ?
                    <CircleCheck className="w-4 h-4 text-green-500"/> :
                    <CircleMinus className="w-4 h-4 text-red-500"/>}</span>;
            },
        },
        {
            accessorKey: "available",
            header: () => <span className="flex items-center justify-center">Available</span>,
            cell: ({row}) => {
                return <span className="flex items-center justify-center">{row.getValue("available") ?
                    <PackageCheck className="w-4 h-4 text-green-500"/> :
                    <CircleMinus className="w-4 h-4 text-red-500"/>
                }</span>;
            },
        },
        {
            id: "actions",
            header: "Action",
            enableHiding: false,
            cell: ({row}) => {
                const product = row.original;
                return (
                    <div className="flex items-center gap-2">
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        className="w-7 h-7 ring-offset-transparent border-default-300 text-default-500"
                                        color="secondary"
                                    >
                                        <Eye className="w-4 h-4"/>
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent side="top">
                                    <p>View</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        className="w-7 h-7 ring-offset-transparent border-default-300 text-default-500"
                                        color="secondary"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedProduct(product);
                                            setShowList(false);
                                        }}
                                    >
                                        <SquarePen className="w-4 h-4"/>
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent side="top">
                                    <p>Edit</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        className="w-7 h-7 ring-offset-transparent border-default-300 text-default-500"
                                        color="secondary"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteProduct(product);
                                        }}
                                    >
                                        <Trash2 className="w-4 h-4"/>
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent
                                    side="top"
                                    className="bg-destructive text-destructive-foreground"
                                >
                                    <p>Delete</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                );
            },
        },
    ];

    const table = useReactTable({
        data,
        columns,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
        onRowSelectionChange: setRowSelection,
        onExpandedChange: setExpanded,
        state: {
            sorting,
            columnFilters,
            columnVisibility,
            rowSelection,
            expanded,
        },
    });

    const toggleRowExpansion = (rowId: string) => {
        table.setExpanded((old) => ({
            ...(old as { [key: string]: boolean }),
            [rowId]: !old[rowId],
        }));
    };

    return (
        <>
            {showList ? (
                <div className="w-full h-screen mb-10">
                    <SiteBreadcrumb/>
                    <div className="flex-1 space-y-1 md:flex md:space-y-0 items-center flex-wrap gap-2 py-4 px-5">
                        <div className="flex-1 text-xl font-medium text-default-900 whitespace-nowrap">
                            Listed Products
                        </div>
                        <div className="flex-none">
                            <Input
                                placeholder="Search..."
                                value={(table.getColumn("name")?.getFilterValue() as string) ?? ""}
                                onChange={(event) =>
                                    table.getColumn("name")?.setFilterValue(event.target.value)
                                }
                                className="max-w-sm"
                            />
                        </div>
                        <div className="flex-none">
                            <Select
                                value={productAvailability || ""}
                                onValueChange={(value) => setProductAvailability(value)}
                            >
                                <SelectTrigger className="border rounded px-4 py-1">
                                    <SelectValue placeholder="Product Availability"/>
                                </SelectTrigger>
                                <SelectContent>

                                    <SelectItem value="available">Mark as Available</SelectItem>
                                    <SelectItem value="unavailable">Mark as Unavailable</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex-none">
                            <Button
                                onClick={handleAvailabilityUpdate}
                                disabled={productAvailability === "" || selectedProductsId.length === 0}
                                className='h-8'
                            >
                                Set
                            </Button>
                        </div>
                    </div>

                    <Table>
                        <TableHeader className="bg-default-200">
                            {table.getHeaderGroups().map((headerGroup) => (
                                <TableRow key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => {
                                        return (
                                            <TableHead key={header.id}>
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(
                                                        header.column.columnDef.header,
                                                        header.getContext()
                                                    )}
                                            </TableHead>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </TableHeader>
                        <TableBody>
                            {table.getRowModel().rows?.length ? (
                                table.getRowModel().rows.map((row) => (
                                    <React.Fragment key={row.id}>
                                        <TableRow
                                            data-state={row.getIsSelected() && "selected"}
                                            onClick={() => toggleRowExpansion(row.id)}
                                            className="cursor-pointer"
                                        >
                                            {row.getVisibleCells().map((cell) => (
                                                <TableCell key={cell.id} className="h-[75px]">
                                                    {flexRender(
                                                        cell.column.columnDef.cell,
                                                        cell.getContext()
                                                    )}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                        <TransitionGroup component={null}>
                                            {row.getIsExpanded() ? (
                                                <CSSTransition
                                                    key={row.id}
                                                    timeout={300}
                                                    classNames="expand"
                                                >
                                                    <TableRow>
                                                        <TableCell colSpan={columns.length}>
                                                            {/* Expanded content */}
                                                            <div className="p-4">
                                                                <div className="flex gap-4">
                                                                    <img
                                                                        src={row.original.image_url}
                                                                        alt={row.original.name}
                                                                        className="w-32 h-32 object-cover"
                                                                    />
                                                                    <div>
                                                                        <h3 className="text-lg font-bold">
                                                                            {row.original.name}
                                                                        </h3>
                                                                        <p>
                                                                            <strong>Category:</strong>{" "}
                                                                            {row.original.category}
                                                                        </p>
                                                                        <p>
                                                                            <strong>Price:</strong>{" "}
                                                                            {CURRENCY}{row.original.price}
                                                                        </p>
                                                                        <p>
                                                                            <strong>Featured:</strong>{" "}
                                                                            {row.original.featured ? "Yes" : "No"}
                                                                        </p>
                                                                        <p>
                                                                            <strong>Today's Deal:</strong>{" "}
                                                                            {row.original.today_special ? "Yes" : "No"}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                                <div className="mt-4">
                                                                    <strong>Description:</strong>
                                                                    <p>{row.original.description}</p>
                                                                </div>
                                                            </div>
                                                        </TableCell>
                                                    </TableRow>
                                                </CSSTransition>
                                            ) : null}
                                        </TransitionGroup>
                                    </React.Fragment>
                                ))
                            ) : isLoading ? (
                                <TableRow>
                                    <TableCell className="h-[75px]" colSpan={columns.length}>
                                        <Loading/>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                <TableRow>
                                    <TableCell
                                        colSpan={columns.length}
                                        className="h-24 text-center"
                                    >
                                        No Items In Your Inventory.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination table={table}/>

                    {/* Delete Dialog */}
                    <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>Delete Product</DialogTitle>
                            </DialogHeader>
                            <p>Are you sure you want to delete this product?</p>
                            <DialogFooter>
                                <Button variant="outline" onClick={cancelDeleteProduct}>
                                    Cancel
                                </Button>
                                <Button variant="outline" onClick={confirmDeleteProduct}>
                                    Delete
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>
            ) : (
                selectedProduct && renderView(selectedProduct)
            )}
        </>
    );
};

export default StorboltProductListingComponent;
